import { Card, Col, Progress, Radio, Row, Select, Typography } from "antd";
import { useEffect, useState } from "react";
import { MonitorStatus, Option } from "src/entity/fugitive/fugitive.entity";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { getMonitorStatus } from "src/services/fugitive.service";
import "./MonitoringProgress.css";
import { profileStore } from "src/stores/profile-store";
import { componentPalette } from "src/theme/component-palette";
import { useShallow } from "zustand/react/shallow";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { generateYearArray } from "src/utils/array";
import { settingStore } from "src/stores/setting-store";
import { CheckboxGroupProps } from "antd/es/checkbox";

const { Text, Title } = Typography;

const MonitoringProgress = () => {
  const profile = profileStore(useShallow((state) => state.profile));
  const loadingProfile = profileStore(useShallow((state) => state.loading));
  const { permissions } = useNavigationMenu();
  const [companyId, setCompanyId] = useState<string>("");
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [optionYears, setOptionYears] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [dataStatus, setDataStatus] = useState<MonitorStatus>();
  const [categories, setCategories] = useState<String[]>([]);
  const [chartSeries, setChartSeries] = useState<{ name: string; data: (number | null)[] }[]>([{ name: "", data: [] }]);
  const [selectedEmission, setSelectedEmission] = useState<string>("tvoc");
  const [optionEmissions, setOptionEmissions] = useState<Option[]>([]);
  const [totalOfMonth, setTotalOfMonth] = useState<number>(0);

  const emissionMaster = settingStore(useShallow((state) => state.emission));
  const getEmission = settingStore(useShallow((state) => state.getEmission));

  const [filterMaintenPending, setFilterMaintenPending] = useState<string>("all");
  const filterOptions: CheckboxGroupProps<string>["options"] = [
    { label: "All", value: "all" },
    { label: "More than 0", value: "more-than-0" },
  ];

  const [pendingArea, setPendingArea] = useState<any[]>([]);
  const [pendingCompany, setPendingCompany] = useState<{ [key: string]: any }>({});

  const fetchYear = () => {
    const years = generateYearArray();
    setOptionYears(years as number[]);
    setSelectedYear(years[0] as number);
  };

  const fetchMaster = async () => {
    await getEmission({ type: "global", label: "Emission" });
  };

  const fetchMonitorStatus = async () => {
    try {
      if (selectedYear) {
        setLoading(true);
        const response = await getMonitorStatus(selectedYear, selectedEmission);
        if (response.data.code === 200) {
          const data = response.data.data;
          if (data && data?.month?.length > 0) {
            const categories = data?.month?.map((item: any) => item.month.charAt(0).toUpperCase() + item.month.slice(1));
            // const series = [
            //   {
            //     name: "Pass",
            //     data: data?.month?.map((item: any) => item.pass), // Data for "Pass"
            //   },
            //   {
            //     name: "Fixed Pass",
            //     data: data?.month?.map((item: any) => item.fixedPass), // Data for "FIxed Pass"
            //   },
            //   {
            //     name: "Over STD",
            //     data: data?.month?.map((item: any) => item.overStd), // Data for "Over STD"
            //   },
            // ];

            let total = 0;
            data?.month?.forEach((item: any) => {
              total += item.pass + item.fixedPass + item.overStd;
            });
            setTotalOfMonth(total);
            const series = data?.month
              ? [
                  {
                    name: "Pass",
                    data: data?.month?.map((item: any) => {
                      return total ? (item.pass / total) * 100 : 0;
                    }), // Data for "Pass" in percentage
                  },
                  {
                    name: "Fixed Pass",
                    data: data?.month?.map((item: any) => {
                      return total ? (item.fixedPass / total) * 100 : 0;
                    }), // Data for "Fixed Pass" in percentage
                  },
                  {
                    name: "Over STD",
                    data: data?.month?.map((item: any) => {
                      return total ? (item.overStd / total) * 100 : 0;
                    }), // Data for "Over STD" in percentage
                  },
                ]
              : [];
            setCategories(categories);
            setChartSeries(series);
            // setChartSeries([
            //   { name: "Pass", data: [10000, 0, 0, 6, 0, 9, 0, 0, 5, 0, 0, 0] },
            //   { name: "Fixed Pass", data: [1, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0] },
            //   { name: "Over STD", data: [1, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 5] },
            // ]);
            // setChartSeries([
            //   { name: "Pass", data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
            //   { name: "Fixed Pass", data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
            //   { name: "Over STD", data: [1, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 5] },
            // ]);
          } else {
            setCategories([]);
            setChartSeries([{ name: "", data: [] }]);
          }
          setPendingArea(data?.area?.pending ?? []);
          setPendingCompany(data?.company);
          setDataStatus(data);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmission = (value: string) => {
    setSelectedEmission(value);
  };

  const onChangeYear = (value: number) => {
    setSelectedYear(value);
  };

  useEffect(() => {
    fetchYear();
    fetchMaster();
  }, []);

  useEffect(() => {
    if (filterMaintenPending === "all") {
      setPendingArea(dataStatus?.area?.pending ?? []);
      setPendingCompany(dataStatus?.company ?? {});
    } else if (filterMaintenPending === "more-than-0") {
      const filteredArea = dataStatus?.area.pending.filter((area) => area.point && area.point > 0);
      setPendingArea(filteredArea ?? []);
      if (pendingCompany.point && pendingCompany.point > 0) {
        setPendingCompany(pendingCompany);
      } else {
        setPendingCompany({});
      }
    }
  }, [filterMaintenPending]);

  useEffect(() => {
    if (profile?.activeCompany) {
      const fetchData = async () => {
        const companyId = profile.activeCompany._id;
        if (companyId) {
          setSelectedYear(currentYear);
          setCompanyId(companyId);
        }
      };
      fetchData();
    }
  }, [profile?.activeCompany]);

  useEffect(() => {
    if (selectedYear && companyId) {
      fetchMonitorStatus();
    }
  }, [selectedYear, selectedEmission, companyId]);

  useEffect(() => {
    if (emissionMaster[0] && emissionMaster[0].options) {
      const optEmission = [...emissionMaster[0].options];
      setOptionEmissions(optEmission);
    }
  }, [emissionMaster]);

  const renderProgress = (percent: number, name: string, type: "pending" | "progress", point: number) => {
    const unit = type === "progress" ? "%" : " point";
    if (type == "progress") {
      point = percent;
    }
    return (
      <Progress
        type="circle"
        percent={percent}
        strokeWidth={15}
        strokeLinecap="butt"
        strokeColor={type === "progress" ? componentPalette.primary : componentPalette.red}
        format={() => (
          <>
            <Text strong style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
              {name}
            </Text>
            <Text strong style={{ display: "block", fontSize: "12px" }}>
              {point}
              {unit}
            </Text>
          </>
        )}
      />
    );
  };

  const options: ApexOptions = {
    chart: {
      type: "bar",
      stacked: true,
      toolbar: {
        show: false,
      },
    },
    xaxis: {
      categories: categories,
    },
    yaxis: {
      show: false,
      logarithmic: true,
      // logBase: 10,
      min: 0.5,
    },
    colors: ["#75c945", "#19e3f2", "#ff0000"], // Colors for "Pass" "Fixed Pass" and "Over STD"
    legend: {
      position: "top",
      horizontalAlign: "right",
    },
    plotOptions: {
      bar: {
        horizontal: false,
      },
    },
    dataLabels: {
      enabled: false,
    },

    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (value: number, { seriesIndex }: any): string => {
          // Use pre-calculated totalOfMonth
          if (seriesIndex === 0) {
            // Pass
            return `${(totalOfMonth * (value / 100)).toFixed(0)}`; // Revert percentage to the raw value for Pass
          } else if (seriesIndex === 1) {
            // Fixed Pass
            return `${(totalOfMonth * (value / 100)).toFixed(0)}`; // Revert percentage to the raw value for Fixed Pass
          } else if (seriesIndex === 2) {
            // Over STD
            return `${(totalOfMonth * (value / 100)).toFixed(0)}`; // Revert percentage to the raw value for Over STD
          }
          return `${value.toFixed(0)}`; // Default for other cases, ensure it returns a string
        },
      },
    },
    grid: {
      show: false,
    },
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", paddingBottom: "10px" }}>
        <div style={{ display: "flex", gap: "20px" }}>
          <Select
            value={selectedYear}
            style={{ width: 120 }}
            onChange={onChangeYear}
            options={optionYears.map((year) => ({ label: year, value: year }))}
            loading={loading || loadingProfile}
            size="middle"
          />
          <Select
            value={selectedEmission}
            style={{ width: 120 }}
            onChange={handleChangeEmission}
            options={optionEmissions}
            loading={loading || loadingProfile}
            size="middle"
          />
        </div>
      </div>
      <Row gutter={[30, 16]} align="stretch">
        <Col span={12}>
          <Card
            headStyle={{ borderBottom: "none" }}
            title={
              <div style={{ textAlign: "center", paddingTop: "10px" }}>
                <Title level={3} style={{ color: componentPalette.blueGrey }}>
                  VOCs Progress
                </Title>
              </div>
            }
            loading={loading || loadingProfile}
            style={{ height: "100%", minHeight: 300 }}
          >
            <Row gutter={[0, 45]}>
              <Col span={8}>{renderProgress(dataStatus?.company.progress ?? 0, dataStatus?.company.companyName ?? "", "progress", 0)}</Col>
              {dataStatus?.area.progress.map((areaItem) => {
                return <Col span={8}>{renderProgress(areaItem.progress, areaItem.areaName, "progress", 0)}</Col>;
              })}
            </Row>
          </Card>
        </Col>
        <Col span={12}>
          <Card
            headStyle={{ borderBottom: "none" }}
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "30px",
                  paddingTop: "10px",
                }}
              >
                <Title level={3} style={{ color: componentPalette.blueGrey, margin: 0 }}>
                  Maintenance Pending
                </Title>
                <Radio.Group
                  className="custom-radio-group"
                  options={filterOptions}
                  defaultValue="all"
                  optionType="button"
                  buttonStyle="solid"
                  value={filterMaintenPending}
                  onChange={(e) => setFilterMaintenPending(e.target.value)}
                />
              </div>
            }
            loading={loading || loadingProfile}
            style={{ height: "100%", minHeight: 300 }}
          >
            <Row gutter={[0, 45]}>
              <Col span={8}>
                {Object.keys(pendingCompany).length > 0 &&
                  renderProgress(pendingCompany.pending ?? 0, pendingCompany.companyName ?? "", "pending", pendingCompany.point ?? 0)}
                {/* {renderProgress(
                  dataStatus?.company.pending ?? 0,
                  dataStatus?.company.companyName ?? "",
                  "pending",
                  dataStatus?.company?.point ?? 0,
                )} */}
              </Col>
              {pendingArea.map((areaItem) => {
                return <Col span={8}>{renderProgress(areaItem.progress, areaItem.areaName, "pending", areaItem.point ?? 0)}</Col>;
              })}
            </Row>
          </Card>
        </Col>
        <Col span={24}>
          <Card
            headStyle={{ borderBottom: "none" }}
            title={
              <div style={{ textAlign: "center", paddingTop: "10px" }}>
                <Title level={3} style={{ color: componentPalette.blueGrey }}>
                  Monthly Progress (points)
                </Title>
              </div>
            }
            loading={loading || loadingProfile}
          >
            <ReactApexChart options={options} series={chartSeries} type="bar" height={350} />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MonitoringProgress;
